/**
 * VideoCall — orchestrates an RTCPeerConnection with the Cloudflare Calls
 * SFU via our worker's `/calls/*` proxy.
 *
 * Flow:
 *   1. Caller creates an RTCPeerConnection + getUserMedia stream elsewhere.
 *   2. `start(stream)` adds senders for every track, generates an SDP offer,
 *      POSTs it with a local-track list to the SFU.
 *   3. The SFU returns an SDP answer, which is applied as the remote
 *      description.
 *   4. Frame encryption is a separate concern: callers install
 *      `RTCRtpSender.transform` / `RTCRtpReceiver.transform` wired to
 *      `@chat/web/frame-cipher` using a key derived from the MLS exporter
 *      secret. That hook runs in the browser and isn't covered by unit
 *      tests; the pure-HTTP behaviour below is.
 */
import type { CallsClient, TracksNewResponse } from './calls.js';

export interface VideoCallDeps {
  readonly calls: CallsClient;
  readonly peerConnection: RTCPeerConnection;
}

export interface VideoCallStartResult {
  readonly sessionId: string;
  readonly localTracks: readonly { readonly mid: string; readonly trackName: string }[];
}

export class VideoCall {
  readonly #calls: CallsClient;
  readonly #pc: RTCPeerConnection;
  #sessionId: string | undefined;

  constructor(deps: VideoCallDeps) {
    this.#calls = deps.calls;
    this.#pc = deps.peerConnection;
  }

  get sessionId(): string | undefined {
    return this.#sessionId;
  }

  /**
   * Publish every track on `stream` to a new Cloudflare Calls session.
   */
  async start(stream: MediaStream): Promise<VideoCallStartResult> {
    const session = await this.#calls.createSession();
    this.#sessionId = session.sessionId;

    for (const track of stream.getTracks()) {
      this.#pc.addTrack(track, stream);
    }

    const offer = await this.#pc.createOffer();
    await this.#pc.setLocalDescription(offer);

    const localTracks = stream.getTracks().map((track) => ({
      location: 'local' as const,
      trackName: track.id,
    }));
    const response: TracksNewResponse = await this.#calls.newTracks(session.sessionId, {
      sessionDescription: { sdp: offer.sdp ?? '', type: 'offer' },
      tracks: localTracks,
    });

    if (response.sessionDescription) {
      await this.#pc.setRemoteDescription(response.sessionDescription);
    }
    return {
      sessionId: session.sessionId,
      localTracks: response.tracks ?? [],
    };
  }

  /**
   * Pull one or more remote tracks (i.e. other participants' published
   * tracks) into this session. Returns the new receiver mids.
   */
  async pullRemoteTracks(
    tracks: readonly { readonly sessionId: string; readonly trackName: string }[],
  ): Promise<TracksNewResponse> {
    if (!this.#sessionId) throw new Error('call not started');
    const response = await this.#calls.newTracks(this.#sessionId, {
      tracks: tracks.map((t) => ({
        location: 'remote' as const,
        trackName: t.trackName,
        sessionId: t.sessionId,
      })),
    });
    if (response.sessionDescription) {
      await this.#pc.setRemoteDescription(response.sessionDescription);
      const answer = await this.#pc.createAnswer();
      await this.#pc.setLocalDescription(answer);
      await this.#calls.renegotiate(this.#sessionId, {
        sdp: answer.sdp ?? '',
        type: 'answer',
      });
    }
    return response;
  }

  async stop(): Promise<void> {
    const senders = this.#pc.getSenders();
    if (this.#sessionId) {
      const mids: { mid: string }[] = [];
      for (const sender of senders) {
        const transceiver = this.#pc.getTransceivers().find((t) => t.sender === sender);
        if (transceiver?.mid) mids.push({ mid: transceiver.mid });
      }
      if (mids.length > 0) {
        await this.#calls.closeTracks(this.#sessionId, mids).catch(() => undefined);
      }
    }
    for (const sender of senders) {
      sender.track?.stop();
    }
    this.#pc.close();
    this.#sessionId = undefined;
  }
}
